import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { TranslateProvider } from "@/context/TranslateContext";
import Navbar from "@/components/Navbar";
import ScrollToTop from "@/components/ScrollToTop";
import AccessibilityBar from "@/components/AccessibilityBar";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import SocialPulseCallback from "@/pages/SocialPulseCallback";
import HandoffPage from "@/pages/HandoffPage";
import YouthPublicPage from "@/pages/YouthPublicPage";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Directory from "@/pages/Directory";
import ProfileView from "@/pages/ProfileView";
import ProfileEdit from "@/pages/ProfileEdit";
import Institutions from "@/pages/Institutions";
import InstitutionDetail from "@/pages/InstitutionDetail";
import Groups from "@/pages/Groups";
import GroupDetail from "@/pages/GroupDetail";
import Connections from "@/pages/Connections";
import AffiliationMap from "@/pages/AffiliationMap";
import Resumes from "@/pages/Resumes";
import ResumeView from "@/pages/ResumeView";
import ShortLink from "@/pages/ShortLink";
import AdminCrawler from "@/pages/AdminCrawler";
import FindColleagues from "@/pages/FindColleagues";
import FindRequestDetail from "@/pages/FindRequestDetail";
import CoursePage from "@/pages/CoursePage";
import SiteBuilder from "@/pages/SiteBuilder";
import PublicSite from "@/pages/PublicSite";
import PublicLayout from "@/components/PublicLayout";
import BrandSwap from "@/components/BrandSwap";
import { isIndia } from "@/lib/edition";
import IndiaLanding from "@/pages/public/IndiaLanding";
import About from "@/pages/public/About";
import Countries from "@/pages/public/Countries";
import PublicInstitutions from "@/pages/public/PublicInstitutions";
import KeralaSchools from "@/pages/public/KeralaSchools";
import PersonProfile from "@/pages/PersonProfile";
import FindTeacher from "@/pages/FindTeacher";
import FacultyNetworks from "@/pages/public/FacultyNetworks";
import AlumniGroups from "@/pages/public/AlumniGroups";
import News from "@/pages/public/News";
import InvitePage from "@/pages/public/InvitePage";
import FeaturePage from "@/pages/public/FeaturePage";
import Articles from "@/pages/public/Articles";
import ArticleDetail from "@/pages/public/ArticleDetail";
import ProjectDetail from "@/pages/public/ProjectDetail";
import AlumniPulseImpact from "@/pages/public/AlumniPulseImpact";
import InstitutionReport from "@/pages/public/InstitutionReport";
import ChampionDashboard from "@/pages/ChampionDashboard";
import Memorials from "@/pages/public/Memorials";
import SampleAlumni from "@/pages/public/SampleAlumni";
import MemoryLane from "@/pages/public/MemoryLane";
import MemoryLaneDetail from "@/pages/public/MemoryLaneDetail";
import TeamAccept from "@/pages/TeamAccept";
import SampleInstitution from "@/pages/public/SampleInstitution";
import SuggestInstitution from "@/pages/public/SuggestInstitution";
import MySuggestions from "@/pages/MySuggestions";
import Support from "@/pages/public/Support";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import MemorialDetail from "@/pages/public/MemorialDetail";
import MemorialCreate from "@/pages/public/MemorialCreate";
import MemorialSuggest from "@/pages/public/MemorialSuggest";
import Trending from "@/pages/public/Trending";
import Contact from "@/pages/public/Contact";
import Legal from "@/pages/public/Legal";
import WhyAlumniPulse from "@/pages/public/WhyAlumniPulse";
import Founder from "@/pages/public/Founder";
import MemorialPolicy from "@/pages/public/MemorialPolicy";
import CountryDetail from "@/pages/public/CountryDetail";
import SandboxBuilder from "@/pages/public/SandboxBuilder";
import PublicEvents from "@/pages/public/PublicEvents";
import EventDetail from "@/pages/public/EventDetail";
import AdminContent from "@/pages/AdminContent";
import AdminSiteContent from "@/pages/AdminSiteContent";
import CampaignTicker from "@/pages/embed/CampaignTicker";
import MagicVerify from "@/pages/auth/MagicVerify";

function Protected() {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="min-h-screen">
      <Navbar />
      <Outlet />
    </div>
  );
}

function PublicOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <div className={isIndia ? "App edition-india" : "App"}>
      <AuthProvider>
        <BrowserRouter>
          <TranslateProvider>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<PublicLayout>{isIndia ? <IndiaLanding /> : <Landing />}</PublicLayout>} />
            <Route path="/india" element={<PublicLayout><IndiaLanding /></PublicLayout>} />
            <Route path="/about" element={<PublicLayout><BrandSwap><About /></BrandSwap></PublicLayout>} />
            <Route path="/why-alumnipulse" element={<PublicLayout><BrandSwap><WhyAlumniPulse /></BrandSwap></PublicLayout>} />
            <Route path="/founder" element={<PublicLayout><Founder /></PublicLayout>} />
            <Route path="/countries" element={<PublicLayout><Countries /></PublicLayout>} />
            <Route path="/countries/:country" element={<PublicLayout><CountryDetail /></PublicLayout>} />
            <Route path="/explore/institutions" element={<PublicLayout><PublicInstitutions /></PublicLayout>} />
            <Route path="/kerala-schools" element={<PublicLayout><KeralaSchools /></PublicLayout>} />
            <Route path="/person/:id" element={<PublicLayout><PersonProfile /></PublicLayout>} />
            <Route path="/find-teacher" element={<PublicLayout><FindTeacher /></PublicLayout>} />
            <Route path="/faculty-networks" element={<PublicLayout><FacultyNetworks /></PublicLayout>} />
            <Route path="/institutions/suggest" element={<PublicLayout><SuggestInstitution /></PublicLayout>} />
            <Route path="/alumni-groups" element={<PublicLayout><AlumniGroups /></PublicLayout>} />
            <Route path="/events" element={<PublicLayout><PublicEvents /></PublicLayout>} />
            <Route path="/events/:id" element={<PublicLayout><EventDetail /></PublicLayout>} />
            <Route path="/trending" element={<PublicLayout><Trending /></PublicLayout>} />
            <Route path="/news" element={<PublicLayout><News /></PublicLayout>} />
            <Route path="/invite/:kind/:id" element={<PublicLayout><InvitePage /></PublicLayout>} />
            <Route path="/features/:slug" element={<PublicLayout><FeaturePage /></PublicLayout>} />
            <Route path="/articles" element={<PublicLayout><Articles /></PublicLayout>} />
            <Route path="/articles/:slug" element={<PublicLayout><ArticleDetail /></PublicLayout>} />
            <Route path="/projects/:pid" element={<PublicLayout><ProjectDetail /></PublicLayout>} />
            <Route path="/impact" element={<PublicLayout><AlumniPulseImpact /></PublicLayout>} />
            <Route path="/institutions/:id/report" element={<PublicLayout><InstitutionReport /></PublicLayout>} />
            <Route path="/sample-alumni" element={<PublicLayout><SampleAlumni /></PublicLayout>} />
            <Route path="/memory-lane" element={<PublicLayout><BrandSwap><MemoryLane /></BrandSwap></PublicLayout>} />
            <Route path="/memory-lane/:id" element={<PublicLayout><MemoryLaneDetail /></PublicLayout>} />
            <Route path="/sample-institution" element={<PublicLayout><SampleInstitution /></PublicLayout>} />
            <Route path="/support" element={<PublicLayout><BrandSwap><Support /></BrandSwap></PublicLayout>} />
            <Route path="/payment/success" element={<div className="min-h-screen"><Navbar /><PaymentSuccess /></div>} />
            <Route path="/payment/cancel" element={<div className="min-h-screen"><Navbar /><PaymentCancel /></div>} />
            <Route path="/memorials" element={<PublicLayout><Memorials /></PublicLayout>} />
            <Route path="/memorials/create" element={<PublicLayout><MemorialCreate /></PublicLayout>} />
            <Route path="/memorials/suggest" element={<PublicLayout><MemorialSuggest /></PublicLayout>} />
            <Route path="/memorials/:id" element={<PublicLayout><MemorialDetail /></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><BrandSwap><Contact /></BrandSwap></PublicLayout>} />
            <Route path="/try" element={<PublicLayout><SandboxBuilder /></PublicLayout>} />
            <Route path="/terms" element={<PublicLayout><Legal kind="terms" /></PublicLayout>} />
            <Route path="/privacy" element={<PublicLayout><Legal kind="privacy" /></PublicLayout>} />
            <Route path="/cookie" element={<PublicLayout><Legal kind="cookie" /></PublicLayout>} />
            <Route path="/memorial-policy" element={<PublicLayout><MemorialPolicy /></PublicLayout>} />
            <Route path="/login" element={<PublicOnly><PublicLayout><Login /></PublicLayout></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><PublicLayout><Register /></PublicLayout></PublicOnly>} />
            <Route path="/auth/socialpulse-callback" element={<SocialPulseCallback />} />
            <Route path="/auth/handoff" element={<HandoffPage />} />
            <Route path="/auth/magic" element={<PublicLayout><MagicVerify /></PublicLayout>} />
            <Route path="/y/:code" element={<PublicLayout><YouthPublicPage /></PublicLayout>} />
            <Route path="/s/:code" element={<ShortLink />} />
            <Route path="/embed/ticker/:pid" element={<CampaignTicker />} />
            <Route path="/r/:slug" element={<ResumeView />} />
            <Route path="/u/:slug" element={<div className="min-h-screen"><Navbar /><ProfileView /></div>} />
            <Route path="/institutions/:id" element={<div className="min-h-screen"><Navbar /><InstitutionDetail /></div>} />
            <Route path="/team/accept" element={<div className="min-h-screen"><Navbar /><TeamAccept /></div>} />
            <Route path="/courses/:id" element={<div className="min-h-screen"><Navbar /><CoursePage /></div>} />
            <Route path="/site/:slug" element={<PublicSite />} />

            <Route element={<Protected />}>
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/directory" element={<Directory />} />
              <Route path="/find" element={<FindColleagues />} />
              <Route path="/find/r/:slug" element={<FindRequestDetail />} />
              <Route path="/profile" element={<ProfileView />} />
              <Route path="/profile/edit" element={<ProfileEdit />} />
              <Route path="/institutions" element={<Institutions />} />
              <Route path="/my-suggestions" element={<MySuggestions />} />
              <Route path="/institutions/:id/build" element={<SiteBuilder />} />
              <Route path="/groups" element={<Groups />} />
              <Route path="/groups/:id" element={<GroupDetail />} />
              <Route path="/champion" element={<div className="min-h-screen"><Navbar /><ChampionDashboard /></div>} />
              <Route path="/connections" element={<Connections />} />
              <Route path="/network" element={<AffiliationMap />} />
              <Route path="/resumes" element={<Resumes />} />
              <Route path="/admin/crawler" element={<AdminCrawler />} />
              <Route path="/admin/content" element={<AdminContent />} />
              <Route path="/admin/site-content" element={<AdminSiteContent />} />
            </Route>
            <Route path="*" element={<PublicLayout><div className="mx-auto max-w-2xl px-5 py-24 text-center"><h1 className="font-heading text-4xl font-semibold">Page not found</h1><p className="mt-3 text-muted-foreground">The page you're looking for doesn't exist or has moved.</p><a href="/" className="mt-6 inline-block rounded-full bg-primary px-5 py-2 text-primary-foreground">Go home</a></div></PublicLayout>} />
          </Routes>
          <AccessibilityBar />
          </TranslateProvider>
        </BrowserRouter>
        <Toaster position="bottom-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
